import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { Calculator, ArrowRight, CheckCircle, FileText, User, Briefcase, Clock, Phone } from 'lucide-react';

const services = [
  { icon: User, title: 'Individual Tax Preparation', description: 'Accurate, thorough preparation of your personal tax return with attention to every deduction and credit available to you.' },
  { icon: Clock, title: 'Prior-Year Returns', description: 'Need to file returns from previous years? We prepare and file them correctly to bring you current with the IRS.' },
  { icon: FileText, title: 'Amended Returns', description: 'If a mistake was made on a prior filing, we prepare and submit amended returns (Form 1040-X) to correct the record.' },
  { icon: Briefcase, title: 'Self-Employed Tax Returns', description: 'Schedule C, estimated taxes, deductions, and home office — we handle the complexity of self-employment taxation.' },
  { icon: Calculator, title: 'Small Business Returns', description: 'Partnerships, S-corps, and LLCs. We prepare business returns and ensure they coordinate with your personal filing.' },
];

export default function TaxPreparation() {
  useSEO({
    title: 'Tax Preparation Services',
    description: 'Professional tax preparation for individuals, self-employed, and small businesses. Prior-year and amended returns handled with 25+ years of experience.',
  });

  return (
    <div data-testid="tax-preparation-page">
      {/* Hero */}
      <section className="bg-brand-navy py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-5">
                Tax Preparation Services
              </p>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none text-white mb-5">
                Tax Preparation Done Right
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                Some visitors come to us with questions but really need filing help. We prepare individual, self-employed, and small business returns with the care and accuracy your situation requires.
              </p>
              <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="prep-hero-cta"
                  className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md"
                >
                  Book a Tax Preparation Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </AnimatedSection>
            <AnimatedSection delay={0.15} className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1762427354566-2b6902a9fd06?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxmaW5hbmNpYWwlMjBkb2N1bWVudCUyMGNhbGN1bGF0b3J8ZW58MHx8fHwxNzc2MzAyODE5fDA&ixlib=rb-4.1.0&q=85"
                alt="Tax documents and calculator"
                className="w-full h-80 object-cover rounded-lg"
                loading="lazy"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              What We Prepare
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-14 max-w-lg">
              Professional preparation for every type of return
            </h2>
          </AnimatedSection>
          <div className="space-y-4">
            {services.map((service, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="flex items-start gap-5 p-6 border border-slate-200 rounded-lg hover:border-brand-green/30 transition-colors">
                  <div className="w-10 h-10 rounded-md bg-brand-navy/5 flex items-center justify-center flex-shrink-0">
                    <service.icon className="w-5 h-5 text-brand-navy" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-base text-brand-navy mb-1">{service.title}</h3>
                    <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed">{service.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-20 lg:py-28 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-6">
              Why choose TaxPro - Answers for tax preparation?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                '25+ years of filing experience',
                'Attention to every deduction',
                'Clear communication throughout',
                'Coordination with resolution services',
                'Prior-year and amended returns',
                'No surprises on pricing',
              ].map((point, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0" />
                  <span className="text-sm text-slate-700">{point}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4">
              Ready to get your taxes filed?
            </h2>
            <p className="text-base text-slate-500 mb-8 max-w-lg mx-auto">
              Book a consultation and we'll discuss your filing needs, timeline, and pricing.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                <Button className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md">
                  Book a Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="tel:8555892461">
                <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-semibold px-7 py-6 text-base rounded-md">
                  <Phone className="w-4 h-4 mr-2" /> 855-589-2461
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
